/**
 * Agrupación de eventos por categoría o ciudad para las vistas
 * `/eventos/categorias` y `/eventos/ciudades`.
 *
 * Lógica PURA (sin React, sin fetch): recibe la lista ya cargada por
 * `useEvents()` y la reparte en grupos. Así se testea sin montar nada y las
 * páginas quedan finas. La agrupación se hace en el front porque el listado
 * público (`GET /v1/events`) ya trae `category_id`/`category_name` y
 * `city_id`/`city_name` enriquecidos — no hace falta un endpoint nuevo.
 */
import type { EventListItem } from '@/hooks/useEvents';

export type GroupDimension = 'category' | 'city';

export interface EventGroup {
  /** Clave estable del grupo (id de categoría/ciudad, o un centinela). */
  key: string;
  /** Nombre legible para el encabezado. */
  label: string;
  events: EventListItem[];
}

/** Centinela para los eventos sin categoría/ciudad asignada. */
const NO_KEY = '__none__';

function dimensionOf(
  event: EventListItem,
  dimension: GroupDimension,
): { id: string | null; name: string | null } {
  return dimension === 'category'
    ? { id: event.categoryId, name: event.categoryName }
    : { id: event.cityId, name: event.cityName };
}

/**
 * Reparte los eventos en grupos por la dimensión pedida.
 *
 * - La **clave** es el id (estable); si falta, cae al centinela → un único
 *   grupo «sin asignar» con `fallbackLabel`.
 * - El **nombre** se toma del primer evento del grupo que traiga uno.
 * - Los grupos se ordenan por **cantidad de eventos desc**, y a igualdad, por
 *   nombre asc (locale es) — los más nutridos primero, para descubrir mejor.
 * - Dentro de cada grupo se preserva el orden de entrada (el catálogo ya viene
 *   ordenado por fecha).
 */
export function groupEvents(
  events: ReadonlyArray<EventListItem>,
  dimension: GroupDimension,
  fallbackLabel: string,
): EventGroup[] {
  const groups = new Map<string, EventGroup>();
  for (const event of events) {
    const { id, name } = dimensionOf(event, dimension);
    const key = id ?? NO_KEY;
    const existing = groups.get(key);
    if (existing) {
      existing.events.push(event);
      if (!existing.label && name) existing.label = name;
    } else {
      groups.set(key, {
        key,
        label: key === NO_KEY ? fallbackLabel : name ?? fallbackLabel,
        events: [event],
      });
    }
  }
  return [...groups.values()].sort((a, b) => {
    if (b.events.length !== a.events.length) return b.events.length - a.events.length;
    return a.label.localeCompare(b.label, 'es', { sensitivity: 'base' });
  });
}
